// vueui/src/assets/common.ts 

import type { Router } from 'vue-router'
import { isSuperUser } from '../../../shared/utils/roles'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime.js'

dayjs.extend(relativeTime)

interface JWTPayload {
  email?: string;
  "cognito:groups"?: string[];
}

export function formatTstamp(ts: string | number | Date | null | undefined): string {
  if (!ts) return '—'
  const dt = dayjs(ts)
  return `${dt.format('M/D/YYYY, h:mm:ss A')} (${dt.fromNow()})`
}

export function formatTstampMultiline(ts: string | number | Date | null | undefined): string {
  if (!ts) return '—'
  const dt = dayjs(ts)
  return `${dt.format('M/D/YYYY')}\n${dt.format('h:mm:ss A')}\n(${dt.fromNow()})`
}

export function getLocalUserEmail(): string | null {
  const token = sessionStorage.getItem("bpm_auth_token") || localStorage.getItem("bpm_auth_token");
  if (!token) return null;

  try {
    const payloadBase64 = token.split('.')[1];
    const payload = JSON.parse(atob(payloadBase64)) as JWTPayload;
    return payload.email || null;
  } catch (e) {
    console.error("Error decoding token:", e);
    return null;
  }
}

export function getLocalUserRole(): string[] | null {
  const token = getToken();
  if (!token) return null;

  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64)) as JWTPayload;

    const groups = payload["cognito:groups"];
    if (Array.isArray(groups) && groups.length > 0) {
      return groups; // Return all groups for proper admin checking
    }
    return null;
  } catch (e) {
    console.warn("Failed to decode JWT token", e);
    return null;
  }
}

export function saveToken(token: string, rememberMe: boolean = false): void {
  if (rememberMe) {
    localStorage.setItem("bpm_auth_token", token);
    sessionStorage.removeItem("bpm_auth_token");
  } else {
    sessionStorage.setItem("bpm_auth_token", token);
    localStorage.removeItem("bpm_auth_token");
  }
}

export function saveRememberMe(emailAddr: string, rememberMe: boolean = false): void {
  if (rememberMe) {
    localStorage.setItem('rememberedEmail', emailAddr);
  } else {
    localStorage.removeItem('rememberedEmail');
  }
}

export function getRememberedEmail(): string | null {
  return localStorage.getItem('rememberedEmail');
}

export function getToken(): string {
  return (
    sessionStorage.getItem("bpm_auth_token") ||
    localStorage.getItem("bpm_auth_token") ||
    ""
  );
}

export function clearToken(): void {
  localStorage.removeItem('bpm_auth_token');
  sessionStorage.removeItem('bpm_auth_token');
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

export function isSignedIn(): boolean {
  return !!getToken();
}

export function isCurrentUserSuperUser(): boolean {
  return isSuperUser(getLocalUserRole(), getLocalUserEmail())
}

// Redirect preservation utilities
export function saveIntendedPath(path: string): void {
  console.log(`🔐 Saving intended path: ${path}`)
  sessionStorage.setItem('intendedPath', path);
}

export function getIntendedPath(): string | null {
  const path = sessionStorage.getItem('intendedPath');
  console.log(`🔐 Retrieved intended path: ${path}`)
  return path;
}

export function clearIntendedPath(): void {
  console.log(`🔐 Clearing intended path`)
  sessionStorage.removeItem('intendedPath');
}

// Global auth error handler for use across the application
export function handleGlobalAuthError(message: string = 'Your session has expired. Please sign in again.') {
  console.warn('🔐 Global auth error:', message)
  
  // Clear auth state
  clearToken()
  
  // Show user-friendly message (could be enhanced with a toast notification)
  if (typeof window !== 'undefined') {
    // You could integrate with a toast notification system here
    console.log('🔐', message)
  }
  
  // Redirect to sign-in page
  if (typeof window !== 'undefined') {
    window.location.href = '/signin'
  }
}

export function logout(router: Router | null = null): void {
  clearToken();

  if (router) router.push({ name: 'SignIn' });
  else window.location.href = '/signin';
}

export const formatJSON = (obj: unknown): string => {
  try {
    return typeof obj === 'string' ? obj : JSON.stringify(obj, null, 2)
  } catch {
    return String(obj)
  }
}

// External link utilities for generating links that should open in new tabs
export function generateBookRepoLink(repoId: string): string {
  return `https://bpubm.com/book-repo/${repoId}`
}

export function generateExternalBookRepoLink(repoId: string, text: string = 'Manage this book'): string {
  // Generate a markdown link that opens in new tab
  return `[${text}](${generateBookRepoLink(repoId)})`
}

export function generateExternalBookRepoHtmlLink(repoId: string, text: string = 'Manage this book'): string {
  // Generate an HTML link that opens in new tab
  return `<a href="${generateBookRepoLink(repoId)}" target="_blank" rel="noopener noreferrer">${text}</a>`
}

let _cachedImgVersion: string | null = null

export async function getImgVersion(): Promise<string> {
  if (_cachedImgVersion) return _cachedImgVersion
  try {
    const res = await fetch('/version.json', { signal: AbortSignal.timeout(3000) })
    if (res.ok) {
      const data = await res.json()
      _cachedImgVersion = data.vueVersion || '1'
    } else {
      _cachedImgVersion = '1'
    }
  } catch {
    _cachedImgVersion = '1'
  }
  return _cachedImgVersion ?? '1'
}
